// src/constants/servidorApiFieldMap.js

export const SERVIDOR_COLLECTION_NAME = 'DadosServidores';

export const SERVIDOR_REQUIRED_API_FIELDS = [
  'doc_cpf',
  'nome',
  'v_vinculo_id',
  'matricula',
];

export const SERVIDOR_UNIDADE_FIELD_OPTIONS = {
  automatico: {
    value: 'automatico',
    label: 'Automático',
  },
  unidade_orcamentaria_descricao: {
    value: 'unidade_orcamentaria_descricao',
    label: 'Unidade orçamentária',
  },
  projto_atividade_descricao: {
    value: 'projto_atividade_descricao',
    label: 'Projeto / atividade',
  },
  razao: {
    value: 'razao',
    label: 'Razão social / unidade gestora',
  },
};

export const SERVIDOR_FIELD_MAP = {
  cpf: ['doc_cpf', 'cpf'],
  nome: ['nome'],
  nomeSocial: ['nome_social'],
  funcId: ['func_id'],

  vinculoId: ['v_vinculo_id', 'vinculo_id'],
  matricula: ['matricula'],
  matriculaSagres: ['matricula_sagres'],
  matriculaEsocial: ['matricula_esocial'],

  cargo: ['cargo_descricao'],
  cargoId: ['v_cargo_id', 'c_cargo_id'],
  cbo: ['cbo_id'],
  cargaHorariaCargo: ['carga_hor_semanal'],
  cargaHorariaVinculo: ['horas_semanal'],

  situacaoFuncional: ['situacao_funcional'],
  situacaoFolha: ['situacao_folha'],

  admissaoTipo: ['admissao_tipo'],
  admissaoData: ['admissao_data'],
  admissaoPortaria: ['admissao_portaria'],

  demissaoData: ['demissao_data'],
  desligamentoMotivo: ['desligamento_motivo'],

  afastamentoData: ['afastamento_data'],
  afastamentoMotivo: ['afastamento_cod_motivo'],

  regimeTrabalho: ['regime_trabalho'],
  regimePrevidenciario: ['regime_previdenciario'],

  razao: ['razao'],
  unidadeGestoraId: ['unidgest_id'],
  cnpjUnidadeGestora: ['cnpj'],

  unidadeOrcamentariaCodigo: ['unidade_orcamentaria_codigo'],
  unidadeOrcamentariaDescricao: ['unidade_orcamentaria_descricao'],

  projetoAtividadeCodigo: ['projto_atividade_codigo'],
  projetoAtividadeDescricao: ['projto_atividade_descricao'],

  dataNascimento: ['dt_nasc'],
  sexo: ['sexo_id'],
  raca: ['raca_id'],
  estadoCivil: ['estcivil_id'],
  grauInstrucao: ['grauinst_id'],
  nacionalidade: ['nacionalidade_id'],
  deficiente: ['deficiente_id'],

  email: ['email'],
  telefoneFixo: ['fone_fixo'],
  telefoneMovel: ['fone_movel'],

  enderecoLogradouro: ['end_lograd'],
  enderecoNumero: ['end_num'],
  enderecoComplemento: ['end_compl'],
  enderecoBairro: ['end_bairro'],
  enderecoMunicipio: ['end_municipio_id'],
  enderecoUf: ['end_uf'],
  enderecoCep: ['end_cep'],

  maeNome: ['mae_nome'],
  maeCpf: ['mae_cpf'],
  paiNome: ['pai_nome'],
  paiCpf: ['pai_cpf'],

  pisPasep: ['doc_pispasep'],
  rgNumero: ['doc_rg_num'],
  rgDataEmissao: ['doc_rg_dtemissao'],
  rgOrgaoExpedidor: ['doc_rg_exp'],
  rgUfExpedidor: ['doc_rg_expuf'],

  tituloEleitorNumero: ['doc_titele_num'],
  tituloEleitorSecao: ['doc_titele_seccao'],
  tituloEleitorZona: ['doc_titele_zona'],

  cnhNumero: ['doc_cnh_num'],
  cnhCategoria: ['doc_cnh_cat'],
  cnhDataValidade: ['doc_cnh_dt_validade'],
};

export function isEmptyServidorValue(value) {
  if (value === undefined || value === null) return true;

  const normalized = String(value).trim().toUpperCase();

  return (
    normalized === '' ||
    normalized === 'N/A' ||
    normalized === 'NULL' ||
    normalized === 'UNDEFINED'
  );
}

export function cleanCpf(value) {
  return String(value || '').replace(/\D/g, '');
}

export function getServidorValue(servidor, fields = [], fallback = '') {
  if (!servidor || !Array.isArray(fields)) return fallback;

  for (const field of fields) {
    const value = servidor[field];

    if (!isEmptyServidorValue(value)) {
      return value;
    }
  }

  return fallback;
}

export function getServidorRawField(servidor, fieldName, fallback = '') {
  if (!servidor || !fieldName) return fallback;

  const value = servidor[fieldName];

  return isEmptyServidorValue(value) ? fallback : value;
}

export function getServidorCpf(servidor) {
  return cleanCpf(getServidorValue(servidor, SERVIDOR_FIELD_MAP.cpf));
}

export function getServidorVinculoId(servidor) {
  return String(getServidorValue(servidor, SERVIDOR_FIELD_MAP.vinculoId)).trim();
}

export function getServidorMatricula(servidor) {
  return String(getServidorValue(servidor, SERVIDOR_FIELD_MAP.matricula)).trim();
}

export function getServidorNome(servidor) {
  return getServidorValue(servidor, SERVIDOR_FIELD_MAP.nome);
}

export function gerarServidorDocId(servidor) {
  const cpf = getServidorCpf(servidor);
  const vinculoId = getServidorVinculoId(servidor);

  if (!cpf || !vinculoId) {
    throw new Error('Não foi possível gerar o ID do servidor: doc_cpf ou v_vinculo_id ausente.');
  }

  return `${cpf}-${vinculoId}`;
}

export function validarServidorMinimo(servidor) {
  const erros = [];

  const cpf = getServidorCpf(servidor);
  const nome = getServidorNome(servidor);
  const vinculoId = getServidorVinculoId(servidor);
  const matricula = getServidorMatricula(servidor);

  if (!cpf) erros.push('Campo obrigatório ausente: doc_cpf');
  if (!nome) erros.push('Campo obrigatório ausente: nome');
  if (!vinculoId) erros.push('Campo obrigatório ausente: v_vinculo_id');
  if (!matricula) erros.push('Campo obrigatório ausente: matricula');

  return {
    valido: erros.length === 0,
    erros,
    cpf,
    nome,
    vinculoId,
    matricula,
  };
}

export function getUnidadeServidor(servidor, municipioConfig = {}) {
  const campo = municipioConfig?.campoSeparacaoUnidadeServidor || 'automatico';

  if (campo === 'unidade_orcamentaria_descricao') {
    return getServidorRawField(servidor, 'unidade_orcamentaria_descricao', 'Sem unidade informada');
  }

  if (campo === 'projto_atividade_descricao') {
    return getServidorRawField(servidor, 'projto_atividade_descricao', 'Sem unidade informada');
  }

  if (campo === 'razao') {
    return getServidorRawField(servidor, 'razao', 'Sem unidade informada');
  }

  return (
    getServidorRawField(servidor, 'unidade_orcamentaria_descricao') ||
    getServidorRawField(servidor, 'projto_atividade_descricao') ||
    getServidorRawField(servidor, 'razao') ||
    'Sem unidade informada'
  );
}

export function getServidorResumo(servidor, municipioConfig = {}) {
  return {
    id: gerarServidorDocId(servidor),

    cpf: getServidorCpf(servidor),
    nome: getServidorNome(servidor),
    nomeSocial: getServidorValue(servidor, SERVIDOR_FIELD_MAP.nomeSocial),

    vinculoId: getServidorVinculoId(servidor),
    matricula: getServidorMatricula(servidor),

    cargo: getServidorValue(servidor, SERVIDOR_FIELD_MAP.cargo),
    cbo: getServidorValue(servidor, SERVIDOR_FIELD_MAP.cbo),

    situacaoFuncional: getServidorValue(servidor, SERVIDOR_FIELD_MAP.situacaoFuncional),
    situacaoFolha: getServidorValue(servidor, SERVIDOR_FIELD_MAP.situacaoFolha),

    admissaoData: getServidorValue(servidor, SERVIDOR_FIELD_MAP.admissaoData),

    unidade: getUnidadeServidor(servidor, municipioConfig),
    razao: getServidorValue(servidor, SERVIDOR_FIELD_MAP.razao),
    unidadeOrcamentaria: getServidorValue(servidor, SERVIDOR_FIELD_MAP.unidadeOrcamentariaDescricao),
    projetoAtividade: getServidorValue(servidor, SERVIDOR_FIELD_MAP.projetoAtividadeDescricao),
  };
}

export function normalizeServidorSearchText(value) {
  return String(value || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim();
}

export function buildServidorSearchText(servidor, municipioConfig = {}) {
  const resumo = getServidorResumo(servidor, municipioConfig);

  return normalizeServidorSearchText([
    resumo.nome,
    resumo.nomeSocial,
    resumo.cpf,
    resumo.matricula,
    resumo.cargo,
    resumo.situacaoFuncional,
    resumo.situacaoFolha,
    resumo.unidade,
    resumo.razao,
    resumo.unidadeOrcamentaria,
    resumo.projetoAtividade,
    getServidorValue(servidor, SERVIDOR_FIELD_MAP.email),
    getServidorValue(servidor, SERVIDOR_FIELD_MAP.rgNumero),
  ].filter(Boolean).join(' '));
}

export function servidorMatchesSearch(servidor, searchTerm, municipioConfig = {}) {
  const search = normalizeServidorSearchText(searchTerm);

  if (!search) return true;

  return buildServidorSearchText(servidor, municipioConfig).includes(search);
}